import React from 'react'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import clsx from 'clsx'
import { Drawer } from '@mui/material'
import { Header } from './Header'
import AlertGroupings from './AlertGroupings'
import { AlertTableContainer } from './AlertTableContainer'
import { GroupType } from './types/GroupType'
import { AlertType, getAlertType } from './types/AlertType'
import { Alert } from './types/Alert'
import { alertGroupingLocalStorage } from './util/alertGroupingLocalStorage'
import ThemeConfig from '../../theme/ThemeConfig'

const PREFIX = 'AlertPane'

const classes = {
  drawer: `${PREFIX}-drawer`,
  paper: `${PREFIX}-paper`,
  container: `${PREFIX}-container`,
  closed: `${PREFIX}-closed`,
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`& .${classes.drawer}`]: {
    flexShrink: 0,
    order: 99,
    width: drawerWidth,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&$closed': {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginLeft: `-${drawerWidth}`,
    },
  },

  [`&.${classes.paper}`]: {
    width: drawerWidth,
    borderTop: 'none',
    borderLeft: containerBorder,
    borderRight: containerBorder,
    borderBottom: containerBorder,
    overflow: 'hidden',
  },

  [`&.${classes.container}`]: {
    overflow: 'hidden',
    padding: '8px 8px 0 8px',
    height: '100%',
  },

  [`&.${classes.closed}`]: {},
}))

const drawerWidth = '350px'
const containerBorder = `1px solid ${ThemeConfig.palette.secondary.main}`

export interface AlertPaneProps {
  id: string
  alerts: Alert[]
  alertType?: AlertType | 'error' | 'warning'
  loading?: boolean
  onAlertClick?: (args: { fieldId: string; productId: string }) => void
  onClose(): void
  open: boolean
  productsWithAlertsCount?: number
}

export const AlertPane = ({
  id,
  alerts = [],
  alertType: passedAlertType = AlertType.Error,
  loading = false,
  onAlertClick,
  onClose,
  open,
  productsWithAlertsCount,
}: AlertPaneProps) => {
  const alertType = getAlertType(passedAlertType)
  const [selectedGrouping, setSelectedGrouping] = useState<GroupType>(
    alertGroupingLocalStorage.getSelectedGrouping() ?? GroupType.Product,
  )

  return (
    <StyledDrawer
      id={id}
      variant="persistent"
      anchor="right"
      open={open}
      classes={{ paper: classes.paper }}
      className={clsx(classes.drawer, {
        [classes.closed]: !open,
      })}
    >
      <Header
        alertType={alertType}
        onClose={onClose}
        productCount={productsWithAlertsCount}
      />
      <AlertGroupings
        alertType={alertType}
        grouping={selectedGrouping}
        onChange={grouping => {
          alertGroupingLocalStorage.setSelectedGrouping(grouping)
          setSelectedGrouping(grouping)
        }}
      />
      <AlertTableContainer
        groupType={selectedGrouping}
        alertType={alertType}
        onAlertClick={onAlertClick}
        loading={loading}
        alerts={alerts}
      />
    </StyledDrawer>
  )
}
